// AdminKyc — KYC review queue
// Per-item: view documents, approve, reject, request more info

import { useState } from 'react';

interface KycItem {
  id: string; user: string; email: string; country: string; submitted: string;
  docType: string; score: number; liveness: 'passed' | 'failed' | 'pending';
  status: 'pending' | 'in_review' | 'flagged'; flags: string[];
}

const QUEUE: KycItem[] = [
  { id: 'k1', user: 'Aisha Khan',     email: 'aisha@email.com',   country: 'GB', submitted: '2h ago',   docType: 'Passport',  score: 96, liveness: 'passed', status: 'pending',   flags: [] },
  { id: 'k2', user: 'Yuki Tanaka',    email: 'yuki@email.com',    country: 'JP', submitted: '4h ago',   docType: 'ID card',   score: 89, liveness: 'passed', status: 'pending',   flags: [] },
  { id: 'k3', user: 'Carlos Mendoza', email: 'carlos@email.com',  country: 'MX', submitted: '5h ago',   docType: 'Driver',    score: 78, liveness: 'passed', status: 'in_review', flags: ['Address mismatch'] },
  { id: 'k4', user: 'Mia Johansson',  email: 'mia@email.com',     country: 'SE', submitted: '8h ago',   docType: 'Passport',  score: 91, liveness: 'passed', status: 'pending',   flags: [] },
  { id: 'k5', user: 'Ravi Patel',     email: 'ravi@email.com',    country: 'IN', submitted: '12h ago',  docType: 'Passport',  score: 42, liveness: 'failed', status: 'flagged',   flags: ['Liveness failed', 'Low doc score'] },
  { id: 'k6', user: 'Hiroshi Tanaka', email: 'hiroshi@email.com', country: 'JP', submitted: '1d ago',   docType: 'ID card',   score: 94, liveness: 'passed', status: 'pending',   flags: [] },
];

export function AdminKyc() {
  const [items, setItems] = useState(QUEUE);
  const [selected, setSelected] = useState<string | null>(null);

  function approve(id: string) {
    if (!confirm('Approve KYC?')) return;
    setItems((curr) => curr.filter((i) => i.id !== id));
    alert('KYC approved. User notified.');
  }
  function reject(id: string) {
    if (!confirm('Reject KYC? User will need to resubmit.')) return;
    setItems((curr) => curr.filter((i) => i.id !== id));
    alert('KYC rejected. User notified.');
  }
  function requestInfo(id: string) {
    if (!confirm('Request more info from user?')) return;
    alert('Info request sent. User has 7 days to respond.');
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-[22px] font-bold text-white">KYC review queue</h1>
          <p className="text-[12px] text-white/50">{items.length} pending · Avg review time 4.2h</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => alert('Bulk approve all clean')} data-testid="bulk-approve-kyc-all" className="h-9 rounded-[8px] bg-[#34C759] px-3 text-[12px] font-semibold text-white active:opacity-40">Approve all clean</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map((k) => {
          const s = k.score >= 90 ? '#34C759' : k.score >= 70 ? '#FF9500' : '#FF3B30';
          return (
            <div key={k.id} data-testid={`kyc-${k.id}`} className={['rounded-[16px] bg-[#1C1C1E] border p-4', k.status === 'flagged' ? 'border-[#FF3B30]/40' : 'border-white/5'].join(' ')}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-bold text-white">{k.user}</span>
                    <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                      k.status === 'pending' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                      k.status === 'in_review' ? 'bg-[#007AFF]/20 text-[#007AFF]' :
                      'bg-[#FF3B30]/20 text-[#FF3B30]'
                    ].join(' ')}>{k.status.replace('_', ' ')}</span>
                  </div>
                  <div className="text-[11px] text-white/50">{k.email} · {k.country} · {k.submitted}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-white/40">Score</div>
                  <div className="text-[18px] font-bold tabular-nums" style={{ color: s }}>{k.score}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-center mb-2">
                <div className="rounded-[8px] bg-white/5 p-1.5"><div className="text-[9px] uppercase text-white/40">Doc</div><div className="text-[10px] font-semibold text-white">{k.docType}</div></div>
                <div className="rounded-[8px] bg-white/5 p-1.5"><div className="text-[9px] uppercase text-white/40">Liveness</div><div className={['text-[10px] font-semibold', k.liveness === 'passed' ? 'text-[#34C759]' : k.liveness === 'failed' ? 'text-[#FF3B30]' : 'text-white'].join(' ')}>{k.liveness}</div></div>
                <div className="rounded-[8px] bg-white/5 p-1.5"><div className="text-[9px] uppercase text-white/40">Country</div><div className="text-[10px] font-semibold text-white">{k.country}</div></div>
              </div>
              {k.flags.length > 0 && (
                <div className="mb-2 rounded-[8px] bg-[#FF3B30]/10 border border-[#FF3B30]/20 p-2">
                  {k.flags.map((f) => <div key={f} className="text-[10px] text-[#FF3B30]">🚩 {f}</div>)}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setSelected(k.id)} data-testid={`kyc-view-${k.id}`} className="h-8 rounded-full bg-white/5 px-3 text-[11px] font-semibold text-white/80 active:bg-white/10">View docs</button>
                <button onClick={() => approve(k.id)} data-testid={`kyc-approve-${k.id}`} className="h-8 rounded-full bg-[#34C759] px-3 text-[11px] font-semibold text-white active:opacity-40">Approve</button>
                <button onClick={() => requestInfo(k.id)} data-testid={`kyc-info-${k.id}`} className="h-8 rounded-full bg-[#FF9500] px-3 text-[11px] font-semibold text-white active:opacity-40">Request info</button>
                <button onClick={() => reject(k.id)} data-testid={`kyc-reject-${k.id}`} className="h-8 rounded-full bg-[#FF3B30] px-3 text-[11px] font-semibold text-white active:opacity-40">Reject</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
